import type {
  AllowedEmailRow,
  AllowedEmailsQuery,
  CreateAllowedEmailPayload,
  AdminLoginLogRow,
  AdminLoginLogsQuery,
  AdminMeetingRow,
  AdminMeetingsQuery,
  AdminUserRow,
  AdminUsersQuery,
  PaginatedResult,
} from '@ai-meeting/shared'
import { downloadBlob } from '@/utils/meetingMinutesToDocx'
import { apiClient, toApiError } from './api'
import type {
  AdminActivityLogsQuery,
  UserActivityActionCount,
  UserActivityLogEntry,
} from '@ai-meeting/shared'
import { getServiceStrings } from './i18n'

/**
 * Downloads the admin Excel report (all users with their login history and
 * meetings/ratings). Requires an authenticated admin; the backend enforces the
 * role and returns the workbook as a binary attachment.
 */
/**
 * Download the admin Excel report, narrowed to a date range when one is set.
 *
 * The range applies to the activity on every sheet, not to the people listed:
 * a user who joined last year but met this month keeps their row. Omitting both
 * bounds exports everything, which is what this call used to do unconditionally
 * — the screen's filter never reached it.
 *
 * @param range Inclusive bounds as epoch ms; either side may be left out.
 */
export async function exportAdminReport(range: AdminReportRange = {}): Promise<void> {
  try {
    const response = await apiClient.get<Blob>('/api/admin/reports/export', {
      params: range,
      responseType: 'blob',
    })
    downloadBlob(response.data, buildReportFilename())
  } catch (error) {
    throw toApiError(error, getServiceStrings().exportReportError)
  }
}

export interface AdminReportRange {
  dateFrom?: number
  dateTo?: number
}

function buildReportFilename(): string {
  const date = new Date().toISOString().slice(0, 10)
  return `aimeeting-report-${date}.xlsx`
}

/**
 * Fetches the paginated user summary (login/meeting aggregates) for the admin
 * management screen. Backend enforces the admin role.
 */
export async function fetchAdminUsers(
  query: AdminUsersQuery
): Promise<PaginatedResult<AdminUserRow>> {
  try {
    const { data } = await apiClient.get<PaginatedResult<AdminUserRow>>(
      '/api/admin/reports/users',
      { params: query }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().adminLoadError)
  }
}

/**
 * How often each function was used, for the activity tab (BRAI-677).
 *
 * Not paginated, unlike every other report on this screen: the answer is one
 * row per action in a fixed list, so it is the same length however much was
 * recorded.
 */
export async function fetchAdminActivitySummary(
  query: { dateFrom?: number; dateTo?: number } = {}
): Promise<{ data: UserActivityActionCount[] }> {
  try {
    const { data } = await apiClient.get<{ data: UserActivityActionCount[] }>(
      '/api/admin/reports/activity',
      { params: query }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().adminLoadError)
  }
}

/**
 * One page of raw activity rows — who did what, when (BRAI-677).
 *
 * The counterpart to `fetchAdminActivitySummary`: that one is how often, this
 * one is who. Paginated, because unlike the summary this grows with every
 * press. `userId` narrows it to one person, which is what the detail drawer
 * sets.
 */
export async function fetchAdminActivityLogs(
  query: AdminActivityLogsQuery
): Promise<PaginatedResult<UserActivityLogEntry>> {
  try {
    const { data } = await apiClient.get<PaginatedResult<UserActivityLogEntry>>(
      '/api/admin/reports/activity/logs',
      { params: query }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().adminLoadError)
  }
}

/**
 * Fetches paginated login history with method and date-range filters.
 */
export async function fetchAdminLoginLogs(
  query: AdminLoginLogsQuery
): Promise<PaginatedResult<AdminLoginLogRow>> {
  try {
    const { data } = await apiClient.get<PaginatedResult<AdminLoginLogRow>>(
      '/api/admin/reports/login-logs',
      { params: query }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().adminLoadError)
  }
}

/**
 * Fetches paginated meetings with ratings and a date-range filter.
 */
export async function fetchAdminMeetings(
  query: AdminMeetingsQuery
): Promise<PaginatedResult<AdminMeetingRow>> {
  try {
    const { data } = await apiClient.get<PaginatedResult<AdminMeetingRow>>(
      '/api/admin/reports/meetings',
      { params: query }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().adminLoadError)
  }
}

/**
 * Fetches the paginated SSO email allowlist. Internal staff are matched by
 * domain and never appear here — this list is only the external addresses.
 */
export async function fetchAllowedEmails(
  query: AllowedEmailsQuery
): Promise<PaginatedResult<AllowedEmailRow>> {
  try {
    const { data } = await apiClient.get<PaginatedResult<AllowedEmailRow>>(
      '/api/admin/allowed-emails',
      { params: query }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().adminLoadError)
  }
}

/**
 * Allows one external address to sign in.
 *
 * @throws ApiError carrying the server's message — a duplicate comes back as a
 *   409 the form shows inline rather than as a generic failure.
 */
export async function createAllowedEmail(
  payload: CreateAllowedEmailPayload
): Promise<AllowedEmailRow> {
  try {
    const { data } = await apiClient.post<AllowedEmailRow>('/api/admin/allowed-emails', payload)
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().allowedEmailAddFailed)
  }
}

/** Revokes one allowed address. */
export async function deleteAllowedEmail(id: string): Promise<void> {
  try {
    await apiClient.delete(`/api/admin/allowed-emails/${id}`)
  } catch (error) {
    throw toApiError(error, getServiceStrings().allowedEmailRemoveFailed)
  }
}
